import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from "./Sidebar";
import { 
  User, 
  Shield, 
  Mail, 
  Calendar, 
  Search, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  UserCog,
  CalendarFold,
  NotebookTabs,
  Inbox,
  Users,
  LogOut
} from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [updatingUser, setUpdatingUser] = useState(null);
  const navigate = useNavigate();


  const [isAddUserOverlayOpen, setIsAddUserOverlayOpen] = useState(false);
const [newUser, setNewUser] = useState({
  username: "",
  email: "",
  password: "",
  role: "Employee"
});
const [addUserError, setAddUserError] = useState("");
const [isAddingUser, setIsAddingUser] = useState(false);
const [passwordRequirements, setPasswordRequirements] = useState({
  minLength: false,
  hasUppercase: false,
  hasNumber: false,
  hasSpecialChar: false
});
const [isSuccessOverlayOpen, setIsSuccessOverlayOpen] = useState(false);
const [successMessage, setSuccessMessage] = useState("");

  // Updated Auth0 Management API configuration
  const config = {
    domain: "vms-optimus.us.auth0.com",
    clientId: "ZxowKlLpWMVvNbUuzMKooWx0FA5jhxYg", 
    clientSecret: "XTH0sFAFWTqpZe8gYn9PJtVU4rg-6hctN7PROhojbsQJ2ZSVy7Nabk5kZSoihlnm",
    audience: "https://vms-optimus.us.auth0.com/api/v2/",
  };
// Role mappings with direct IDs
const roleMap = {
  "rol_xUv1JEhGgOWMSTUh": "Admin",
  "rol_sNukGt3kUZddyKeO": "Employee",
  "rol_tn4hgiqrbDtFxEY7": "Concierge"
};

const reverseRoleMap = {
  "Admin": "rol_xUv1JEhGgOWMSTUh",
  "Employee": "rol_sNukGt3kUZddyKeO",
  "Concierge": "rol_tn4hgiqrbDtFxEY7"
};
  // Get current user info to determine permissions
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const canManageRoles = currentUser?.role === "Admin";


  const checkPasswordRequirements = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    setPasswordRequirements(requirements);
    return Object.values(requirements).every(req => req);
  };
  
  const handleAddUserClick = () => {
    setNewUser({ username: "", email: "", password: "", role: "Employee" });
    setPasswordRequirements({
      minLength: false,
      hasUppercase: false,
      hasNumber: false,
      hasSpecialChar: false
    });
    setAddUserError("");
    setIsAddingUser(false);
    setIsAddUserOverlayOpen(true);
  };
  
  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    if (!Object.values(passwordRequirements).every(req => req)) {
      return;
    }
  
    setIsAddingUser(true);
    setAddUserError(""); 
  
    try {
      const token = await getAccessToken();
  
      const response = await fetch(`${config.audience}users`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: newUser.username,
          email: newUser.email,
          password: newUser.password,
          connection: "Username-Password-Authentication",
          app_metadata: { role: newUser.role },
          user_metadata: { username: newUser.username },
        }),
      });
  
      if (response.ok) {
        const createdUser = await response.json();
        
        // Assign role
// Assign role
const roleId = reverseRoleMap[newUser.role];        
        await fetch(`${config.audience}users/${createdUser.user_id}/roles`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            roles: [roleId],
          }),
        });
  
        // Add user to local state
        setUsers((prevUsers) => [
          ...prevUsers,
          {
            user_id: createdUser.user_id,
            email: newUser.email,
            username: newUser.username,
            displayRole: newUser.role,
            roleId: roleId,
            lastLogin: 'Never',
            createdAt: new Date().toLocaleDateString(),
          },
        ]);
  
        setNewUser({ username: "", email: "", password: "", role: "Employee" });
        setPasswordRequirements({
          minLength: false,
          hasUppercase: false,
          hasNumber: false,
          hasSpecialChar: false
        });
        setAddUserError("");
        setIsAddUserOverlayOpen(false);
        
        setSuccessMessage("User added successfully!");
        setIsSuccessOverlayOpen(true);
        setTimeout(() => {
          setIsSuccessOverlayOpen(false);
          setSuccessMessage("");
        }, 3000);
  
      } else {
        const errorDetails = await response.json();
        if (response.status === 409 || errorDetails.message?.toLowerCase().includes('already exists')) {
          setAddUserError("A user with this email already exists.");
        } else {
          setAddUserError(errorDetails.message || "Failed to add user. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error adding user:", error);
      setAddUserError("An error occurred while adding the user. Please try again.");
    } finally {
      setIsAddingUser(false);
    }
  };


  // Logout function
  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    navigate("/");
  };

  

  // Fetch access token for Management API
  const getAccessToken = async () => {
    try {
      const response = await fetch(`https://${config.domain}/oauth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: config.clientId,
          client_secret: config.clientSecret,
          audience: config.audience,
          grant_type: "client_credentials",
          scope: "read:users read:users_app_metadata read:user_idp_tokens update:users update:users_app_metadata create:users delete:users read:roles create:roles delete:roles update:roles read:role_members create:role_members delete:role_members",
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to get access token');
      }
      
      const data = await response.json();
      return data.access_token;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  };

  // Fetch all users with their roles
  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = await getAccessToken();
      
      // Fetch users
      const usersResponse = await fetch(`${config.audience}users?per_page=100&include_totals=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!usersResponse.ok) {
        throw new Error('Failed to fetch users');
      }

      const usersData = await usersResponse.json();
      
      // Fetch roles for each user
      const usersWithRoles = await Promise.all(
        usersData.users.map(async (user) => {
          try {
            const rolesResponse = await fetch(`${config.audience}users/${user.user_id}/roles`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            
            const roles = rolesResponse.ok ? await rolesResponse.json() : [];
            const primaryRole = roles.length > 0 ? roleMap[roles[0].id] || 'Unknown' : 'No Role';
            
            return {
              ...user,
              displayRole: primaryRole,
              roleId: roles.length > 0 ? roles[0].id : null,
              username: user.user_metadata?.username || user.email?.split('@')[0] || 'N/A',
              lastLogin: user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never',
              createdAt: new Date(user.created_at).toLocaleDateString(),
            };
          } catch (error) {
            console.error(`Error fetching roles for user ${user.user_id}:`, error);
            return {
              ...user,
              displayRole: 'Error',
              roleId: null,
              username: user.user_metadata?.username || user.email?.split('@')[0] || 'N/A',
              lastLogin: user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never',
              createdAt: new Date(user.created_at).toLocaleDateString(),
            };
          }
        })
      );

      setUsers(usersWithRoles);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update user role
  const updateUserRole = async (userId, newRole) => {
    if (!canManageRoles) {
      setError('You do not have permission to manage roles.');
      return;
    }

    setUpdatingUser(userId);
    setError('');
    
    try {
      const token = await getAccessToken();
      const roleId = reverseRoleMap[newRole];
      
      if (!roleId) {
        throw new Error('Invalid role selected');
      }

      // First, remove all existing roles
      const currentUserData = users.find(u => u.user_id === userId);
      if (currentUserData.roleId) {
        await fetch(`${config.audience}users/${userId}/roles`, {
          method: 'DELETE',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            roles: [currentUserData.roleId]
          }),
        });
      }

      // Then assign the new role
      const assignResponse = await fetch(`${config.audience}users/${userId}/roles`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          roles: [roleId]
        }),
      });

      if (!assignResponse.ok) {
        throw new Error('Failed to assign new role');
      }

      // Update local state
      setUsers(users.map(user => 
        user.user_id === userId 
          ? { ...user, displayRole: newRole, roleId: roleId }
          : user
      ));

      setSuccessMessage(`Successfully updated role for ${currentUserData.username}`);
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error updating user role:', error);
      setError('Failed to update user role. Please try again.');
    } finally {
      setUpdatingUser(null);
    }
  };

  // Filter users based on search query and role filter
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = selectedRole === 'all' || user.displayRole === selectedRole;
    
    return matchesSearch && matchesRole;
  });

  // Get role badge color
// Get role badge color
const getRoleBadgeColor = (role) => {
  switch (role) {
    case 'Admin':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'Employee':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Concierge':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

  useEffect(() => {
    if (currentUser?.role === "Admin") {
      fetchUsers();
    }
  }, []);

 // Show loading or redirect if not admin
if (!currentUser || currentUser.role !== "Admin") {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">You don't have permission to access this page.</p>
          <button
            onClick={() => navigate('/invite')}
            className="px-6 py-2 bg-blue-800 text-white rounded-lg font-medium hover:bg-blue-800 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar onLogout={handleLogout} />
        <div className="ml-32 mr-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-800" />
              <span className="text-gray-600">Loading users...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar onLogout={handleLogout} />

      {/* Success Overlay */}
{isSuccessOverlayOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-lg p-6 w-[400px]">
      <h2 className="text-lg font-semibold text-center text-green-600">
        {successMessage}
      </h2>
    </div>
  </div>
)}

{/* Add User Modal */}
{isAddUserOverlayOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-40" onClick={() => setIsAddUserOverlayOpen(false)}>
    <div className="bg-white rounded-lg shadow-lg p-6 w-[400px]" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg mx-auto font-semibold">Add New User</h2>
        <button className="text-gray-600 hover:text-black" onClick={() => setIsAddUserOverlayOpen(false)}>
          ✕
        </button>
      </div>
      <form onSubmit={handleAddUserSubmit}>
        <input
          type="text"
          placeholder="Username"
          className="w-full border rounded p-2 mb-4"
          value={newUser.username}
          onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
          required
        />
        <input
          type="email"
          placeholder="Email"
          className="w-full border rounded p-2 mb-4"
          value={newUser.email}
          onChange={(e) => {
            setNewUser({ ...newUser, email: e.target.value });
            setAddUserError("");
          }}
          required
        />
        <input
          type="password"
          placeholder="Password"
          className="w-full border rounded p-2 mb-2"
          value={newUser.password}
          onChange={(e) => {
            const password = e.target.value;
            setNewUser({ ...newUser, password });
            checkPasswordRequirements(password);
          }}
          required
        />
        
        {/* Password Requirements */}
        <div className="mb-4 text-sm">
          <p className="mb-1 font-medium text-gray-700">Password must contain:</p>
          <ul className="space-y-0.5 text-xs">
            <li className={passwordRequirements.minLength ? 'text-gray-500' : 'text-red-500'}>
              • At least 8 characters long
            </li>
            <li className={passwordRequirements.hasUppercase ? 'text-gray-500' : 'text-red-500'}>
              • One uppercase letter
            </li>
            <li className={passwordRequirements.hasNumber ? 'text-gray-500' : 'text-red-500'}>
              • One number
            </li>
            <li className={passwordRequirements.hasSpecialChar ? 'text-gray-500' : 'text-red-500'}>
              • One special character (!@#$%^&*(),.?":{}|&lt;&gt;)
            </li>
          </ul>
        </div>
        
        {/* Error Message */}
        {addUserError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{addUserError}</p>
          </div>
        )}
        
        <select
  className="w-full border rounded p-2 mb-4"
  value={newUser.role}
  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
>
  <option value="Employee">Employee</option>
  <option value="Concierge">Concierge</option>
  <option value="Admin">Admin</option>
</select>
        
        <button
          type="submit"
          disabled={!Object.values(passwordRequirements).every(req => req) || isAddingUser}
          className={`text-white w-full py-2 rounded-md ${
            !Object.values(passwordRequirements).every(req => req) || isAddingUser 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-800 hover:bg-blue-800'
          }`}
        >
          {isAddingUser ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Adding User...
            </div>
          ) : (
            'Add User'
          )}
        </button>
      </form>
    </div>
  </div>
)}

      {/* Main Content */}
      <div className="ml-32 mr-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900 flex items-center">
                User Management
              </h1>
            </div>
            <button
      onClick={handleAddUserClick}
      className="bg-blue-800 text-white px-6 py-1 rounded-lg font-medium hover:bg-blue-800 transition-colors flex items-center gap-2"
    >
      <span className="text-xl">+</span> Add User
    </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
            <span className="text-green-700">{successMessage}</span>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by username or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

          {/* Role Filter */}
<div className="sm:w-48">
  <select
    value={selectedRole}
    onChange={(e) => setSelectedRole(e.target.value)}
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  >
    <option value="all">All Roles</option>
    <option value="Admin">Admin</option>
    <option value="Employee">Employee</option>
    <option value="Concierge">Concierge</option>
  </select>
</div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  {canManageRoles && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={canManageRoles ? 5 : 4} className="px-6 py-8 text-center text-gray-500">
                      {searchQuery || selectedRole !== 'all' ? 'No users found matching your filters.' : 'No users found.'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.user_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-blue-800 flex items-center justify-center text-white font-semibold mr-3">
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.username}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.displayRole)}`}>
                          <Shield className="w-3 h-3 mr-1" />
                          {user.displayRole}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {user.lastLogin}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.createdAt}
                      </td>
                      {canManageRoles && (
                        <td className="px-6 py-4 whitespace-nowrap">
                         <select
  value={user.displayRole}
  onChange={(e) => updateUserRole(user.user_id, e.target.value)}
  disabled={
    updatingUser === user.user_id || 
    (user.email === currentUser?.email)
  }
  className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
>
  <option value="Admin">Admin</option>
  <option value="Employee">Employee</option>
  <option value="Concierge">Concierge</option>
</select>
                          {updatingUser === user.user_id && (
                            <RefreshCw className="w-4 h-4 animate-spin text-blue-800 ml-2 inline" />
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Stats */}
        <div className="mt-6 text-sm text-gray-500 text-center">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>
    </div>
  );
};

export default UserManagement;